import React, { useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiBox } from "react-icons/fi";
import axios from "../../../Utils/axiosInstance";
import { Link } from "react-router-dom";

const Categories = () => {
  const scrollRef = useRef();
  const specialScrollRef = useRef();

  const [categories, setCategories] = useState([]);
  const [specialVarieties, setSpecialVarieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const shouldCenterCards = categories.length > 0 && categories.length < 5;

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({
      left: dir === "left" ? -200 : 200,
      behavior: "smooth",
    });
  };

  const scrollSpecial = (dir) => {
    specialScrollRef.current?.scrollBy({
      left: dir === "left" ? -280 : 280,
      behavior: "smooth",
    });
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(false);

      const [popularRes, specialRes] = await Promise.all([
        axios.get("/category/get-all", {
          params: { special: false },
        }),
        axios.get("/category/get-all", {
          params: { special: true, limit: 4 },
        }),
      ]);

      const popularData = popularRes?.data?.data || [];
      const specialCategories = specialRes?.data?.data || [];

      setCategories(popularData);

      const varietyRequests = specialCategories.map((category) =>
        axios.get("/product-variety/get-all", {
          params: {
            categoryId: category._id,
            isActive: true,
            limit: 6,
            populate: true,
          },
        }),
      );

      const specialVarietyResponses = await Promise.all(varietyRequests);
      const specialVarietiesData = specialVarietyResponses.flatMap((resp, index) => {
        const varieties = resp?.data?.data || [];
        return varieties.map((variety) => ({
          ...variety,
          parentCategory: specialCategories[index],
        }));
      });

      setSpecialVarieties(specialVarietiesData);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <div className="w-full pb-14 md:pb-16 px-4 md:px-8">
      <div className="2xl:max-w-7xl mx-auto">
        {/* TITLE - ORIGINAL DESIGN */}
        <div className="text-center mb-12">
          <div className="flex flex-col items-center gap-2">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-[#3D0F1B] to-subPrimaryColor py-2">
              Our Popular Categories
            </h2>
          </div>
          <p className="text-xs md:w-[60%] mx-auto md:text-sm text-gray-500 mt-2 leading-relaxed">
            The Best Platform For B2B Business Growth And Global Trade
            Opportunities. Create Your Requirements Here And Wait For The Best
            Quotation
          </p>
        </div>

        {/* LOADING SKELETON */}
        {loading && (
          <div className=" flex gap-5 md:gap-0 lg:gap-4 overflow-x-auto xl:justify-center">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="min-w-[11.5rem] sm:min-w-[10rem] md:min-w-[11rem] flex flex-col items-center"
              >
                {/* CIRCLE SKELETON */}
                <div className="relative w-full h-[11.5rem] md:w-36 md:h-36 lg:w-40 lg:h-40 rounded-full bg-gradient-to-b from-[#FFEDEE] to-[#FFF7F7] flex items-end justify-center overflow-hidden">
                  {/* shimmer effect */}
                  <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/60 to-transparent" />

                  {/* fake image block */}
                  <div className="absolute bottom-[-20px] w-[10rem] md:w-28 lg:w-36 h-[6rem] md:h-[5rem] lg:h-[6rem] bg-gray-200 rounded-md" />
                </div>

                {/* TEXT SKELETON */}
                <div className="mt-6 h-4 w-24 bg-gray-200 rounded-md animate-pulse" />
              </div>
            ))}
          </div>
        )}

        {/* ERROR UI */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="text-4xl mb-3">📡</div>

            <p className="text-sm font-medium text-gray-700">
              Failed to load categories
            </p>

            <p className="text-xs text-gray-400 mt-1 mb-4">
              Please check your connection and try again
            </p>

            <button
              onClick={fetchCategories}
              className="px-5 py-2 bg-primaryColor text-white rounded-xl text-sm"
            >
              Retry
            </button>
          </div>
        )}

        {/* SUCCESS */}
        {!loading && !error && (
          <>
            {/* EMPTY STATE */}
            {categories.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-5 md:py-8 text-center">
                {/* Icon */}
                <div className="text-5xl mb-4">📦</div>

                {/* Title */}
                <p className="text-base font-semibold text-gray-700">
                  No Categories Found
                </p>

                {/* Subtitle */}
                <p className="text-sm text-gray-400 mt-1 mb-5 max-w-sm">
                  Categories will appear here once they are created. Stay tuned
                  or check back later.
                </p>

                {/* Optional Button (you can remove if not needed) */}
                <button
                  onClick={fetchCategories}
                  className="px-5 py-2 bg-primaryColor text-white rounded-xl text-sm"
                >
                  Refresh
                </button>
              </div>
            ) : (
              <>
                <div className="">
                  <div
                    ref={scrollRef}
                    className={`gap-5 flex ${shouldCenterCards ? "justify-center" : "justify-start"} md:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 max-w-full`}
                    style={{ WebkitOverflowScrolling: "touch" }}
                  >
                    {categories.map((item, i) => (
                      <Link
                        to={`/products?category=${item._id}`}
                        key={item._id || i}
                        className="min-w-[10.6rem] md:min-w-[10rem] lg:min-w-[11.3rem] 2xl:min-w-[10.5rem] flex flex-col items-center snap-center group"
                      >
                        {/* Enhanced Circle Container */}
                        <div className="relative w-[10.5rem] h-[10.5rem] sm:w-[11.5rem] sm:h-[11.5rem] md:w-36 md:h-36 lg:w-40 lg:h-40 rounded-full overflow-hidden bg-gradient-to-b from-[#FFEDEE] to-[#FFF7F7] flex items-end justify-center shadow-md group-hover:shadow-xl transition-all duration-300 border-2 border-white group-hover:border-[#3D0F1B]/10">

                          {/* Image with improved effects */}
                          <img
                            src={item.img || "/fallback.png"}
                            alt={item.name}
                            className="object-cover remove-white-bg h-full w-full transition-all duration-500 ease-out group-hover:-translate-y-2 group-hover:scale-110 group-hover:drop-shadow-[0_8px_16px_#924d5f]"
                          />

                          {/* Shine effect on hover */}
                          <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-300 bg-gradient-to-t from-transparent to-white via-transparent" />
                        </div>

                        {/* Enhanced Text */}
                        <p className="mt-6 text-base max-w-xs md:text-lg text-primaryColor font-semibold md:font-semibold text-center group-hover:text-[#3D0F1B] transition-colors duration-300">
                          {item.name}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* BUTTONS */}
                <div className="flex items-center justify-center gap-3 mt-8">
                  <button
                    onClick={() => scroll("left")}
                    className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor transition"
                  >
                    <FiChevronLeft />
                  </button>

                  <Link
                    to={`/products`}
                    className="
                      flex items-center gap-2 px-4 py-2 rounded-full
                      border-2 border-primaryColor text-primaryColor
                      text-sm font-semibold
                      hover:bg-primaryColor hover:text-white
                      transition-all duration-200 active:scale-95
                    "
                  >
                    View All
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primaryColor/10 text-xs font-bold">
                      {categories.length}
                    </span>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                    </svg>
                  </Link>

                  <button
                    onClick={() => scroll("right")}
                    className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor transition"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </>
            )}

            {specialVarieties.length > 0 && (
              <div className="mt-16">
                <div className="mb-8 text-center">
                  <h3 className="text-3xl md:text-4xl lg:text-5xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-[#3D0F1B] to-subPrimaryColor py-2">
                    Plant Extracts
                  </h3>
                  <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-gray-500 md:text-base">
                    Discover our range of high-quality plant extracts, carefully sourced for their potency and purity.
                  </p>
                </div>

                <div className="px-0 md:px-0">
                  <div
                    ref={specialScrollRef}
                    className="flex gap-4 overflow-x-auto scroll-smooth pb-2 snap-x snap-mandatory"
                  >
                    {specialVarieties.map((item, i) => {
                      const categoryId =
                        item.productId?.subCategoryId?.categoryId?._id ||
                        item.parentCategory?._id;
                      const subCategoryId = item.productId?.subCategoryId?._id;
                      const productId = item.productId?._id;

                      return (
                        <Link
                          to={`/products?category=${categoryId}&subCategory=${subCategoryId}&product=${productId}&variety=${item._id}`}
                          key={item._id || i}
                          className="group w-[85%] shrink-0 sm:w-[49%] lg:w-[24%] snap-center overflow-hidden rounded-[1.5rem] bg-white hover:shadow-lg shadow-sm transition duration-300"
                        >
                          <div className="relative h-44 overflow-hidden bg-gradient-to-br from-[#FFF4F6] to-[#FDEFF2] sm:h-48">
                            {item.img ? (
                              <img
                                src={item.img}
                                alt={item.varietyName}
                                className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110 group-hover:translate-y-[-5px]"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#FFEDEE] to-[#F9F0F2]">
                                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/80 shadow-sm">
                                  <FiBox className="h-9 w-9 text-primaryColor" />
                                </div>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-[#3D0F1B]/20 via-transparent to-transparent" />
                          </div>

                          <div className="px-4 py-5 text-center sm:px-5">
                            <h4 className="text-lg font-semibold text-[#3D0F1B] group-hover:text-primaryColor">
                              {item.varietyName}
                            </h4>
                            <p className="mt-1 text-sm text-gray-500">
                              {item.productId?.name || item.parentCategory?.name}
                            </p>
                          </div>
                        </Link>
                      );
                    })}
                  </div>

                  <div className="mt-8 flex items-center justify-center gap-3">
                    <button
                      onClick={() => scrollSpecial("left")}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-primaryBtnColor text-white transition hover:bg-primaryBtnHoverColor md:h-9 md:w-9"
                    >
                      <FiChevronLeft />
                    </button>

                    <Link
                      to="/special-varieties"
                      className="flex items-center gap-2 rounded-full border-2 border-primaryColor px-4 py-2 text-sm font-semibold text-primaryColor transition-all duration-200 hover:bg-primaryColor hover:text-white active:scale-95"
                    >
                      View All
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primaryColor/10 text-xs font-bold">
                        {specialVarieties.length}
                      </span>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                      </svg>
                    </Link>

                    <button
                      onClick={() => scrollSpecial("right")}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-primaryBtnColor text-white transition hover:bg-primaryBtnHoverColor md:h-9 md:w-9"
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Categories;